import cloneDeep from 'clone-deep';
import deepMerge from 'deepmerge';
import flatten, { unflatten } from 'flat';
import IFields from '@core/interfaces/IFields';
import { ShapeId } from '@core/constants/Shape';
import { FieldDimensionId } from '@core/constants/FieldDimension';
import {
    IUserKey,
    IUser,
} from '../interfaces/User';
import {
    IFormFieldContactKey,
    IFormFieldContact,
    IFormFieldShapeDimension,
    IFormFieldDimension,
    IDimensionUnit,
    IForm,
    IFormFieldKey,
} from '../interfaces/Form';

export function getDimensionUnitLabel(value: number, unit: IDimensionUnit = IDimensionUnit.Millimeters): string {
    switch (unit) {
    case IDimensionUnit.Meters:
        return `${value / 1000} ${IDimensionUnit.Meters}`;
    case IDimensionUnit.Decimeters:
        return `${value / 100} ${IDimensionUnit.Decimeters}`;
    case IDimensionUnit.Degrees:
        return `${value / 100} ${IDimensionUnit.Degrees}`;
    case IDimensionUnit.SquareMeter:
        return `${value / 100} ${IDimensionUnit.SquareMeter}`;
    case IDimensionUnit.Humidity:
        return `${value / 100} ${IDimensionUnit.Humidity}`;
    case IDimensionUnit.Centimeters:
        return `${value / 10} ${IDimensionUnit.Centimeters}`;
    default:
        return `${value} ${IDimensionUnit.Millimeters}`;
    }
};

export function getFormFieldContactKeyFromUserKey(userKey: IUserKey): IFormFieldContactKey | undefined {
    switch (userKey) {
    case IUserKey.FirstName: return IFormFieldContactKey.FirstName;
    case IUserKey.LastName: return IFormFieldContactKey.LastName;
    case IUserKey.Email: return IFormFieldContactKey.Email;
    case IUserKey.Phone: return IFormFieldContactKey.Phone;
    case IUserKey.ShippingAddress: return IFormFieldContactKey.ShippingAddress;
    case IUserKey.ShippingAddressCity: return IFormFieldContactKey.ShippingAddressCity;
    case IUserKey.ShippingAddressZip: return IFormFieldContactKey.ShippingAddressZip;
    case IUserKey.ShippingAddressCountry: return IFormFieldContactKey.ShippingAddressCountry;
    default: return undefined;
    }
}

export function getMergedFormFieldContactAndUser(formFieldContact: IFormFieldContact, user?: IUser | null): IFormFieldContact {
    if (!user) return formFieldContact;

    const userContact = cloneDeep(formFieldContact);
    const userEntries = Object.entries(user);

    userEntries.forEach(([userKey, userValue]) => {
        const contactKey = getFormFieldContactKeyFromUserKey(userKey as IUserKey);
        if (contactKey && userContact[contactKey]) {
            // @ts-ignore
            userContact[contactKey].value = userValue;
        }
    });

    if (!user.phone) userContact.phone.value = undefined;

    return userContact;
}

export function getFormWithOnlyFieldsValues(form: Partial<IForm>): Partial<IForm> & { fields: IFields} {
    return {
        ...form,
        fields: unflatten(Object.fromEntries(Object
            .entries(flatten(form.fields, { safe: true }))
            .filter(([key, value]) => (key.endsWith('value')) && value !== undefined)
            .map(([key, value]) => [key.slice(0, key.lastIndexOf('.value')), value]))),
    };
}

export function getMergedStaticFormWidthFieldsValues(form: IForm, fields: IFields): IForm {
    const safeFields = Object.fromEntries(Object.entries(fields)
        .filter(([key, _]) => !!form.fields[key as IFormFieldKey])) as IFields;

    return {
        ...form,
        fields: deepMerge(form.fields, unflatten(Object.fromEntries(Object
            .entries(flatten(safeFields, { safe: true }))
            .map(([key, value]) => [`${key}.value`, value])))),
    };
}

export function getFormFieldDimensionFromShapeDimension(formField: IFormFieldShapeDimension): IFormFieldDimension {
    const baseFormFieldDimension = formField.dimension.options[formField.shape.value as ShapeId];

    return Object.fromEntries(Object.entries(formField.dimension)
        .filter(([dimensionKey, value]) => !!value && Object.values(FieldDimensionId).includes(dimensionKey as FieldDimensionId))
        .map(([dimensionKey, value]) => [dimensionKey, {
            ...baseFormFieldDimension?.[dimensionKey as FieldDimensionId],
            ...value,
        }]));
}

export function removeFormFields(form: IForm, formFieldKeys: IFormFieldKey[]): IForm {
    return {
        ...form,
        fieldsOrder : form.fieldsOrder.filter((formFieldKey) => !formFieldKeys.includes(formFieldKey)),
        fields      : {
            ...Object.fromEntries(Object.entries(form.fields).filter(([formFieldKey]) => !formFieldKeys.includes(formFieldKey as IFormFieldKey))),
        },
    } as IForm;
}

export default getFormFieldContactKeyFromUserKey;
